// Widgets — Homescreen der Pille (Handoff Pille §5, Screens 12/21): große Widgets für
// Zeiterfassung, Telefon und Team Chat, darunter die App-Icons. Ein Klick öffnet die App als
// Unterseite des Decks (User-Wunsch 2026-08-12). Widgets zeigen nur vorhandene Zahlen.
import { useState, type CSSProperties, type ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ChatCircleDots, Pause, Phone, PhoneX, Play, Stop, Timer, type Icon } from '@phosphor-icons/react';
import { useChat } from '../../../contexts/ChatContext';
import { useZeit } from '../../../contexts/ZeitContext';
import { stundenText } from '../../../lib/zeiterfassung/berechnung';
import { gruppenName, initialen, kollegeName, vorschauText } from '../../../lib/chat/nachrichten';
import { zeitKurz, zuListenEintrag } from '../../../lib/telefonie/anrufListe';
import { chatFuerWidget, zeitRing } from '../../../lib/island/homescreen';
import { stempelFehlerKurz, stempelFehlerText, type EreignisArt } from '../../../lib/island/zeitApp';
import { useIslandActions, type DeckPage } from '../IslandProvider';
import { useVerpassteAnrufe, type VerpassteAnrufe } from '../useVerpassteAnrufe';
import { FARBE } from '../pille-stil';
import { FiggoFigur } from '../Figgo';
import { setzeFiggoAn, useFiggoAn } from '../figgoSignal';

const KARTE: CSSProperties = {
  height: 172, borderRadius: 26, padding: 15, position: 'relative', overflow: 'hidden', minWidth: 0,
  display: 'flex', flexDirection: 'column',
  boxShadow: 'inset 0 0.5px 0 rgba(255,255,255,0.22), inset 0 0 0 0.5px rgba(255,255,255,0.08), 0 14px 30px -16px rgba(0,0,0,0.8)',
};
function schein(rgb: string): string {
  return `radial-gradient(120% 90% at 0% 0%, rgba(${rgb},0.32), transparent 62%), linear-gradient(160deg, rgba(255,255,255,0.1), rgba(255,255,255,0.035))`;
}

const APP_VERLAUF = {
  chat: 'linear-gradient(160deg,#5ac8fa,#0a6fd8)',
  zeit: 'linear-gradient(160deg,#ff9f0a,#d36b00)',
  telefon: 'linear-gradient(160deg,#34c759,#1f8f3c)',
} as const;

const RING_U = 2 * Math.PI * 40;

function Einblenden({ i, className, style, children }: { i: number; className?: string; style?: CSSProperties; children: ReactNode }) {
  const ruhig = useReducedMotion() ?? false;
  return (
    <motion.div className={className} style={style}
      initial={ruhig ? false : { opacity: 0, y: 14, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5, delay: [0, 0.07, 0.14, 0.2][i] ?? 0.2, ease: [0.32, 0.72, 0, 1] }}>
      {children}
    </motion.div>
  );
}

/** Kopfzeile eines Widgets: kleines App-Icon + Name. */
function WidgetKopf({ icon: I, verlauf, titel, rechts = 0 }: { icon: Icon; verlauf: string; titel: string; rechts?: number }) {
  return (
    <span className="flex items-center" style={{ gap: 7, fontSize: 12, fontWeight: 700, color: 'rgba(235,235,245,0.75)', minWidth: 0, paddingRight: rechts }}>
      <span className="flex shrink-0 items-center justify-center" style={{ width: 22, height: 22, borderRadius: 7, background: verlauf }} aria-hidden>
        <I size={13} weight="fill" color="#fff" />
      </span>
      <span className="truncate">{titel}</span>
    </span>
  );
}

/** Ganze Karte klickbar über einen Knopf in der Fläche; Inhalte darüber nehmen keine Klicks (außer eigenen Knöpfen). */
function KartenKnopf({ label, onClick }: { label: string; onClick: () => void }) {
  return <button type="button" aria-label={label} onClick={onClick} className="pille-wknopf absolute inset-0 cursor-pointer" style={{ border: 'none', background: 'transparent', borderRadius: 26 }} />;
}

function ZeitWidget({ oeffnen }: { oeffnen: () => void }) {
  const { laufend, inPause, laufendeMinuten, heuteMinuten, heuteSollMinuten, saldoMinuten, bereit, starten, beenden, pauseUmschalten } = useZeit();
  const ruhig = useReducedMotion() ?? false;
  const [arbeitet, setArbeitet] = useState(false);
  const [fehler, setFehler] = useState<EreignisArt | null>(null);
  const ring = zeitRing(heuteMinuten, heuteSollMinuten);
  const status = laufend ? (inPause ? 'Pause läuft' : 'Arbeitszeit läuft') : 'Heute gearbeitet';
  const punkt = laufend ? (inPause ? '#ffb35c' : FARBE.gruen) : 'rgba(235,235,245,0.4)';

  /** Stempeln; scheitert das Speichern, ersetzt ein Hinweis die Saldo-Zeile, bis erneut gestempelt wird. */
  async function tue(fn: () => Promise<boolean>, art: EreignisArt) {
    setArbeitet(true);
    setFehler(null);
    let ok = false;
    try { ok = await fn(); } catch { ok = false; } finally { setArbeitet(false); }
    if (!ok) setFehler(art);
  }
  const knopf: CSSProperties = { flex: 1, height: 34, borderRadius: 12, border: 'none', gap: 5, fontFamily: 'inherit', fontSize: 12, fontWeight: 800, cursor: 'pointer', pointerEvents: 'auto', opacity: arbeitet ? 0.6 : 1 };

  return (
    <div data-bj="widget" className="pille-wkarte" style={{ ...KARTE, background: schein('255,159,10') }}>
      <KartenKnopf label="Zeiterfassung öffnen" onClick={oeffnen} />
      <div className="pointer-events-none relative flex h-full flex-col">
        <WidgetKopf icon={Timer} verlauf={APP_VERLAUF.zeit} titel="Zeiterfassung" />
        <div className="flex min-h-0 flex-1 items-center" style={{ gap: 14 }}>
          <div className="relative shrink-0" style={{ width: 96, height: 96 }}>
            <svg width="96" height="96" viewBox="0 0 100 100" style={{ transform: 'rotate(-90deg)' }} aria-hidden>
              <defs>
                <linearGradient id="pille-zeitring" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#ffd27a" /><stop offset="1" stopColor="#ff8a00" /></linearGradient>
              </defs>
              <circle cx="50" cy="50" r="40" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="10" />
              <motion.circle cx="50" cy="50" r="40" fill="none" stroke="url(#pille-zeitring)" strokeWidth="10" strokeLinecap="round"
                strokeDasharray={RING_U}
                initial={ruhig ? false : { strokeDashoffset: RING_U }}
                animate={{ strokeDashoffset: RING_U * (1 - ring.anteil) }}
                transition={{ duration: 1.1, delay: 0.2, ease: [0.32, 0.72, 0, 1] }} />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="tabular-nums" style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1, color: laufend && !inPause ? FARBE.gold : '#fff' }}>
                {bereit ? stundenText(laufend ? laufendeMinuten : heuteMinuten).replace(' h', '') : '–'}
              </span>
              <span style={{ fontSize: 10, fontWeight: 700, color: 'rgba(235,235,245,0.5)', marginTop: 2 }}>{ring.prozentText}</span>
            </div>
          </div>
          <div className="flex min-w-0 flex-1 flex-col" style={{ gap: 6 }}>
            <span className="flex items-center whitespace-nowrap" style={{ gap: 6, fontSize: 13, fontWeight: 800 }}>
              <span className={laufend && !inPause ? 'pille-puls-punkt' : undefined} style={{ width: 7, height: 7, borderRadius: '50%', background: punkt }} aria-hidden />
              {status}
            </span>
            <span className="truncate" style={{ fontSize: 12, fontWeight: 600, color: FARBE.text2 }}>Soll {stundenText(heuteSollMinuten)} · {ring.restText}</span>
            {fehler ? (
              <span role="alert" aria-label={stempelFehlerText(fehler)} title={stempelFehlerText(fehler)} className="truncate" style={{ fontSize: 12, fontWeight: 700, color: '#ff8a80' }}>{stempelFehlerKurz(fehler)}</span>
            ) : <span className="truncate" style={{ fontSize: 12, fontWeight: 600, color: FARBE.text2 }}>
              Saldo <b style={{ color: saldoMinuten > 0 ? '#5fd38a' : saldoMinuten < 0 ? FARBE.orange : '#fff' }}>{stundenText(saldoMinuten, true)}</b>
            </span>}
            {bereit && (
              <div className="flex" style={{ gap: 6, marginTop: 2 }}>
                {!laufend ? (
                  <button type="button" disabled={arbeitet} onClick={() => void tue(starten, 'kommen')} className="pille-druck flex items-center justify-center" style={{ ...knopf, background: FARBE.gruen, color: '#06210f' }}>
                    <Play size={13} weight="fill" /> Kommen
                  </button>
                ) : (
                  <>
                    <button type="button" disabled={arbeitet} onClick={() => void tue(pauseUmschalten, inPause ? 'weiter' : 'pause')} className="pille-druck flex items-center justify-center"
                      style={{ ...knopf, background: inPause ? 'linear-gradient(160deg,#f1dcb4,#d4ad73 55%,#9c7440)' : 'rgba(255,255,255,0.12)', color: inPause ? '#231806' : '#fff' }}>
                      <Pause size={13} weight="fill" /> {inPause ? 'Weiter' : 'Pause'}
                    </button>
                    <button type="button" disabled={arbeitet} onClick={() => void tue(beenden, 'gehen')} className="pille-druck flex items-center justify-center" style={{ ...knopf, background: 'rgba(255,69,58,0.9)', color: '#fff' }}>
                      <Stop size={13} weight="fill" /> Gehen
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function TelefonWidget({ oeffnen, verpasst }: { oeffnen: () => void; verpasst: VerpassteAnrufe }) {
  const { anzahl, letzter } = verpasst;
  const eintrag = letzter ? zuListenEintrag(letzter) : null;
  return (
    <div className="pille-wkarte" style={{ ...KARTE, background: schein('52,199,89') }}>
      <KartenKnopf label="Telefon öffnen" onClick={oeffnen} />
      <div className="pointer-events-none relative flex h-full flex-col">
        <WidgetKopf icon={Phone} verlauf={APP_VERLAUF.telefon} titel="Telefon" rechts={30} />
        <Phone size={22} weight="fill" className={anzahl > 0 ? 'pille-klingeln absolute' : 'absolute'} style={{ right: -1, top: -1, color: 'rgba(52,199,89,0.9)' }} aria-hidden />
        <div className="mt-auto flex min-w-0 flex-col" style={{ gap: 2 }}>
          <span className="tabular-nums" style={{ fontSize: 36, fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 1 }}>{anzahl}</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: FARBE.text2 }}>verpasste Anrufe</span>
          {eintrag ? (
            <div className="flex min-w-0 flex-col" style={{ marginTop: 6, gap: 1 }}>
              <span className="flex min-w-0 items-center" style={{ gap: 5, fontSize: 12, fontWeight: 700, color: FARBE.rotHell }}>
                <PhoneX size={12} weight="bold" className="shrink-0" aria-hidden /><span className="truncate">{eintrag.titel}</span>
              </span>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'rgba(235,235,245,0.45)', paddingLeft: 17 }}>{zeitKurz(eintrag.begonnenAm, Date.now())}</span>
            </div>
          ) : (
            <span style={{ marginTop: 6, fontSize: 12, fontWeight: 700, color: FARBE.gruen }}>Alles erledigt</span>
          )}
        </div>
      </div>
    </div>
  );
}

function ChatWidget({ oeffnen }: { oeffnen: () => void }) {
  const { unterhaltungen, kollegen, gruppen, meineId, ungelesen, oeffneZiel } = useChat();
  const u = chatFuerWidget(unterhaltungen);
  const name = !u ? '' : u.art === 'gruppe'
    ? (() => { const g = gruppen.find(x => x.id === u.id); return g ? gruppenName(g) : 'Gruppe'; })()
    : kollegeName(kollegen.find(k => k.id === u.id));
  const von = u?.letzte ? (u.letzte.absender_id === meineId ? 'Du' : kollegeName(kollegen.find(k => k.id === u.letzte?.absender_id))) : '';

  return (
    <div className="pille-wkarte" style={{ ...KARTE, background: schein('10,132,255') }}>
      <KartenKnopf label="Team Chat öffnen" onClick={() => { if (u) oeffneZiel({ art: u.art, id: u.id }); oeffnen(); }} />
      <div className="pointer-events-none relative flex h-full flex-col">
        <WidgetKopf icon={ChatCircleDots} verlauf={APP_VERLAUF.chat} titel="Team Chat" rechts={ungelesen > 0 ? 26 : 0} />
        {ungelesen > 0 && (
          <span className="pille-badge absolute flex items-center justify-center tabular-nums" aria-label={`${ungelesen} ungelesen`}
            style={{ right: -3, top: -3, minWidth: 22, height: 22, padding: '0 6px', borderRadius: 11, fontSize: 11, fontWeight: 800, background: FARBE.rot }}>
            {ungelesen > 99 ? '99+' : ungelesen}
          </span>
        )}
        {!u?.letzte && <span className="mt-auto" style={{ fontSize: 12, fontWeight: 700, color: FARBE.text2 }}>Keine Nachrichten</span>}
        {u?.letzte && (
          <div className="mt-auto flex min-w-0 flex-col" style={{ gap: 8 }}>
            <div className="flex min-w-0 items-center" style={{ gap: 8 }}>
              <span className="flex shrink-0 items-center justify-center rounded-full" aria-hidden style={{ width: 28, height: 28, fontSize: 10, fontWeight: 800, background: APP_VERLAUF.chat }}>{initialen(name)}</span>
              <span className="truncate" style={{ fontSize: 13, fontWeight: 800 }}>{name}</span>
            </div>
            <div className="flex max-w-full flex-col self-start" style={{ padding: '8px 11px', borderRadius: '16px 16px 16px 5px', background: 'rgba(255,255,255,0.12)', gap: 1 }}>
              <span style={{ fontSize: 10.5, fontWeight: 800, color: '#64d2ff' }}>{von}</span>
              <span className="pille-zweizeilig" style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.35, color: 'rgba(255,255,255,0.9)' }}>{vorschauText(u.letzte)}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

interface App { key: string; label: string; icon: Icon; ziel: DeckPage; verlauf: string; badge?: number; punkt?: string | null; klingeln?: boolean }

export function WidgetsPage() {
  const { setDeckPage } = useIslandActions();
  const { ungelesen } = useChat();
  const { laufend, inPause } = useZeit();
  const verpasstStand = useVerpassteAnrufe();
  const verpasst = verpasstStand.anzahl;
  const ruhig = useReducedMotion() ?? false;

  const apps: App[] = [
    { key: 'chat', label: 'Team Chat', icon: ChatCircleDots, ziel: 'chat', verlauf: APP_VERLAUF.chat, badge: ungelesen },
    { key: 'zeit', label: 'Zeiterfassung', icon: Timer, ziel: 'zeit', verlauf: APP_VERLAUF.zeit, punkt: laufend ? (inPause ? '#ffb35c' : '#5fd38a') : null },
    { key: 'telefon', label: 'Telefon', icon: Phone, ziel: 'telefon', verlauf: APP_VERLAUF.telefon, badge: verpasst, klingeln: verpasst > 0 },
  ];

  return (
    <div className="flex h-full flex-col overflow-y-auto text-white" style={{ padding: '4px 16px 18px', gap: 18 }}>
      <div className="grid grid-cols-2 sm:grid-cols-4" style={{ gap: 12 }}>
        <Einblenden i={0} className="col-span-2 min-w-0"><ZeitWidget oeffnen={() => setDeckPage('zeit')} /></Einblenden>
        <Einblenden i={1} className="min-w-0"><TelefonWidget oeffnen={() => setDeckPage('telefon')} verpasst={verpasstStand} /></Einblenden>
        <Einblenden i={2} className="min-w-0"><ChatWidget oeffnen={() => setDeckPage('chat')} /></Einblenden>
      </div>

      <Einblenden i={3} className="flex flex-col" style={{ gap: 12 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: FARBE.text2, padding: '0 4px' }}>Apps</span>
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: '16px 8px' }}>
          {apps.map((a, i) => {
            const I = a.icon;
            return (
              <motion.button key={a.key} type="button" onClick={() => setDeckPage(a.ziel)}
                aria-label={a.badge ? `${a.label}, ${a.badge} neu` : a.label}
                className="pille-app flex cursor-pointer flex-col items-center"
                style={{ gap: 7, border: 'none', background: 'transparent', fontFamily: 'inherit', color: '#fff' }}
                initial={ruhig ? false : { opacity: 0, scale: 0.4, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ type: 'spring', stiffness: 420, damping: 20, delay: 0.12 + i * 0.07 }}>
                <span className="pille-app-icon relative flex items-center justify-center"
                  style={{ width: 64, height: 64, borderRadius: 19, background: `linear-gradient(180deg, rgba(255,255,255,0.32), rgba(255,255,255,0) 52%), ${a.verlauf}`, boxShadow: 'inset 0 0.5px 0 rgba(255,255,255,0.5), 0 10px 20px -10px rgba(0,0,0,0.8)' }}>
                  <span className="pointer-events-none absolute inset-0 overflow-hidden" style={{ borderRadius: 19 }} aria-hidden>
                    <span className="pille-glanz absolute bottom-0 left-0 top-0" style={{ width: '40%', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)', animationDelay: `${1.2 + i * 0.6}s` }} />
                  </span>
                  <I size={a.key === 'chat' ? 34 : 31} weight="fill" color="#fff" className={a.klingeln ? 'pille-klingeln' : undefined} aria-hidden />
                  {!!a.badge && a.badge > 0 && (
                    <span className="pille-badge absolute flex items-center justify-center tabular-nums" aria-hidden
                      style={{ top: -6, right: -6, minWidth: 22, height: 22, padding: '0 6px', borderRadius: 11, fontSize: 12, fontWeight: 800, background: FARBE.rot, boxShadow: '0 0 0 2.5px #0a0a0d' }}>
                      {a.badge > 99 ? '99+' : a.badge}
                    </span>
                  )}
                  {a.punkt && (
                    <span className={a.punkt === '#5fd38a' ? 'pille-puls-punkt absolute' : 'absolute'} aria-hidden
                      style={{ top: -3, right: -3, width: 15, height: 15, borderRadius: '50%', background: a.punkt, boxShadow: '0 0 0 2.5px #0a0a0d' }} />
                  )}
                </span>
                <span className="text-center" style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.82)' }}>{a.label}</span>
              </motion.button>
            );
          })}
        </div>
      </Einblenden>

      <FiggoSchalter />
    </div>
  );
}

/** Einstellung „Figgo anzeigen“ (localStorage, Standard an). Bei reduzierter Bewegung bleibt er ohnehin aus. */
function FiggoSchalter() {
  const an = useFiggoAn();
  return (
    <button type="button" role="switch" aria-checked={an} onClick={() => setzeFiggoAn(!an)}
      className="pille-zeile flex w-full cursor-pointer items-center"
      style={{ minHeight: 56, gap: 12, padding: '8px 14px', borderRadius: 18, border: 'none', fontFamily: 'inherit', color: '#fff', textAlign: 'left' }}>
      <span className="flex shrink-0 items-center justify-center" aria-hidden style={{ width: 36, height: 36, borderRadius: 11, background: 'rgba(255,255,255,0.08)' }}>
        <FiggoFigur pose="stand" s={0.8} />
      </span>
      <span className="min-w-0 flex-1" style={{ fontSize: 14, fontWeight: 700 }}>Figgo anzeigen</span>
      <span aria-hidden className="relative shrink-0" style={{ width: 51, height: 31, borderRadius: 16, background: an ? FARBE.gruen : 'rgba(120,120,128,0.36)', transition: 'background .25s' }}>
        <span className="absolute rounded-full" style={{ top: 2, left: an ? 22 : 2, width: 27, height: 27, background: '#fff', boxShadow: '0 3px 8px rgba(0,0,0,0.25)', transition: 'left .25s cubic-bezier(.34,1.4,.64,1)' }} />
      </span>
    </button>
  );
}
